import { Button } from '@mantine/core';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { SegmentedButtons } from '@/components/Ui/SegmentedButtons';
import { useUndoToast } from '@/components/Ui/Toast';
import { useLanguageAwareRouting } from '@/hooks/useLanguageAwareRouting';
import { useUnsavedChangesWarning } from '@/hooks/useUnsavedChangesWarning';
import {
  type FormField,
  type FormSpec,
  type FormSpecParseResult,
  parseFormSpecToml,
  validatFormSpec,
} from '@/lib/form-specs';
import { FieldCanvas } from './FieldCanvas';
import classes from './FieldEditor.module.css';
import { FieldPalette } from './FieldPalette';
import { FieldPropertiesPanel } from './FieldPropertiesPanel';
import { FormSettingsPanel } from './FormSettingsPanel';
import {
  addField,
  duplicateField,
  type FieldKind,
  moveField,
  newField,
  normalizeToml,
  patchField,
  removeField,
  renameField,
  serializeSpec,
  withSettings,
} from './model';

type View = 'visual' | 'toml';
type TomlError = Extract<FormSpecParseResult, { ok: false }>;

const EMPTY_SPEC: FormSpec = { fields: {}, settings: { discard_additional_fields: false } };

export interface FieldEditorProps {
  formId: string;
  formTitle: string;
  initialSpec?: string;
  /** Rejects when the save failed; the API layer has already reported why. */
  onSave: (specs: string) => Promise<void>;
}

function initialState(initialSpec: string) {
  const parsed = parseFormSpecToml(initialSpec);
  if (!parsed.ok) {
    return { spec: EMPTY_SPEC, toml: initialSpec, error: parsed, view: 'toml' as View };
  }
  const spec = withSettings(parsed.spec);
  return { spec, toml: serializeSpec(spec), error: null, view: 'visual' as View };
}

export function FieldEditor({ formId, formTitle, initialSpec = '', onSave }: FieldEditorProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { createLanguageURL } = useLanguageAwareRouting();
  const notify = useUndoToast();

  const [init] = useState(() => initialState(initialSpec));
  const [baseline] = useState(() => normalizeToml(initialSpec));
  const [spec, setSpec] = useState<FormSpec>(init.spec);
  const [toml, setToml] = useState(init.toml);
  const [tomlError, setTomlError] = useState<TomlError | null>(init.error);
  const [view, setView] = useState<View>(init.view);
  const [selectedKey, setSelectedKey] = useState<string | null>(
    () => Object.keys(init.spec.fields)[0] ?? null
  );
  const [blockedSwitch, setBlockedSwitch] = useState(false);
  const [saving, setSaving] = useState(false);

  const dirty = useMemo(() => normalizeToml(toml) !== baseline, [toml, baseline]);
  const hasFields = Object.keys(spec.fields).length > 0;
  const problems = useMemo(
    () => (hasFields ? [...validatFormSpec(spec).errors.entries()] : []),
    [spec, hasFields]
  );
  const canSave = dirty && !tomlError && problems.length === 0 && !saving;

  const overviewPath = createLanguageURL(`/account/forms/${formId}`);
  const allowLeave = useUnsavedChangesWarning({
    when: dirty && !saving,
    title: t('fields.leaveTitle'),
    body: t('fields.leaveBody'),
    confirmLabel: t('fields.leaveConfirm'),
    cancelLabel: t('fields.leaveCancel'),
  });

  const applySpec = (next: FormSpec) => {
    const withDefaults = withSettings(next);
    setSpec(withDefaults);
    setToml(serializeSpec(withDefaults));
    setTomlError(null);
  };

  const onTomlChange = (text: string) => {
    setToml(text);
    const parsed = parseFormSpecToml(text);
    if (!parsed.ok) {
      setTomlError(parsed);
      return;
    }
    setTomlError(null);
    setBlockedSwitch(false);
    setSpec(parsed.spec);
    if (!selectedKey || !parsed.spec.fields[selectedKey]) {
      setSelectedKey(Object.keys(parsed.spec.fields)[0] ?? null);
    }
  };

  const onViewChange = (next: View) => {
    if (next === 'visual' && tomlError) {
      setBlockedSwitch(true);
      return;
    }
    setView(next);
  };

  const onAdd = (kind: FieldKind) => {
    const field = newField(kind, t(`fields.kind.${kind}`), Object.keys(spec.fields));
    applySpec(addField(spec, field.name, field));
    setSelectedKey(field.name);
  };

  const onPatch = (key: string) => (patch: Partial<FormField>) =>
    applySpec(patchField(spec, key, patch));

  const onRename = (key: string) => (name: string) => {
    applySpec(renameField(spec, key, name));
    setSelectedKey(name);
  };

  const onDuplicate = (key: string) => () => {
    const result = duplicateField(spec, key);
    applySpec(result.spec);
    setSelectedKey(result.key);
  };

  const onRemove = (key: string) => () => {
    const before = spec;
    const keys = Object.keys(spec.fields);
    const idx = keys.indexOf(key);
    applySpec(removeField(spec, key));
    setSelectedKey(keys[idx + 1] ?? keys[idx - 1] ?? null);
    notify(t('fields.removed', { name: spec.fields[key]?.name ?? key }), () => {
      applySpec(before);
      setSelectedKey(key);
    });
  };

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await onSave(toml.trim() === '' ? '' : toml);
    } catch {
      setSaving(false);
      return;
    }
    allowLeave();
    notify(t('fields.saved'));
    navigate(overviewPath);
  };

  const selected = selectedKey ? spec.fields[selectedKey] : undefined;
  const takenNames = useMemo(() => new Set(Object.keys(spec.fields)), [spec]);

  const problemList = problems.length > 0 && (
    <div className={classes.problems} role="alert">
      <strong>{t('fields.fixBeforeSaving')}</strong>
      <ul>
        {problems.map(([key, message]) => (
          <li key={key}>
            <span className={classes.mono}>{key}</span>: {message}
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <div className={classes.shell}>
      <header className={classes.header}>
        <div className={classes.headerSide}>
          <Link to={overviewPath} className={classes.back}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
            {formTitle}
          </Link>
          <h1 className={classes.title}>{t('fields.pageTitle')}</h1>
          <SegmentedButtons<View>
            aria-label={t('fields.editorView')}
            options={[
              { value: 'visual', label: t('fields.visual') },
              { value: 'toml', label: t('fields.toml') },
            ]}
            value={view}
            onChange={onViewChange}
          />
        </div>
        <div className={classes.headerActions}>
          <span className={classes.rh} aria-live="polite">
            {dirty ? t('fields.unsaved') : t('fields.noChanges')}
          </span>
          <Button variant="default" onClick={() => navigate(overviewPath)} disabled={saving}>
            {t('fields.cancel')}
          </Button>
          <Button onClick={() => void save()} loading={saving} disabled={!canSave}>
            {t('fields.save')}
          </Button>
        </div>
      </header>

      {view === 'toml' ? (
        <div className={classes.tomlWrap}>
          <p className={classes.sub} style={{ margin: 0 }}>
            {t('fields.tomlHint')}
          </p>
          <textarea
            className={`${classes.tomlText} ${tomlError ? classes.tomlTextError : ''}`}
            aria-label={t('fields.tomlLabel')}
            aria-invalid={tomlError ? true : undefined}
            aria-describedby={tomlError ? 'toml-error' : undefined}
            value={toml}
            onChange={(e) => onTomlChange(e.currentTarget.value)}
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
          />
          {tomlError && (
            <div id="toml-error" className={classes.problems} role="alert">
              {tomlError.line !== undefined
                ? t('fields.tomlErrorAt', {
                    line: tomlError.line,
                    column: tomlError.column ?? 1,
                    error: tomlError.error,
                  })
                : tomlError.error}
              {blockedSwitch && <div>{t('fields.tomlFixFirst')}</div>}
            </div>
          )}
          {!tomlError && problemList}
        </div>
      ) : (
        <>
          {problemList && <div className={classes.problemsWrap}>{problemList}</div>}
          <div className={classes.body}>
            <FieldPalette onAdd={onAdd} />
            <FieldCanvas
              spec={spec}
              selectedKey={selectedKey}
              onSelect={setSelectedKey}
              onMove={(from, to) => applySpec(moveField(spec, from, to))}
              footer={
                <FormSettingsPanel
                  spec={spec}
                  onDiscardChange={(discard) =>
                    applySpec({
                      ...spec,
                      settings: { ...spec.settings, discard_additional_fields: discard },
                    })
                  }
                />
              }
            />
            {selected && selectedKey ? (
              <FieldPropertiesPanel
                key={selectedKey}
                fieldKey={selectedKey}
                field={selected}
                takenNames={takenNames}
                onPatch={onPatch(selectedKey)}
                onRename={onRename(selectedKey)}
                onDuplicate={onDuplicate(selectedKey)}
                onRemove={onRemove(selectedKey)}
              />
            ) : (
              <aside className={`${classes.panel} ${classes.props}`}>
                <span className={classes.sub}>{t('fields.nothingSelected')}</span>
              </aside>
            )}
          </div>
        </>
      )}
    </div>
  );
}
